import store from 'store'
import { getPersonCustomFieldLabels, getCompanyCustomFieldLabels, getDealCustomFieldLabels } from 'selectors/Account'

class ChangedAttributesFinder {
  constructor (oldAttrs, cfls) {
    this.oldAttrs = oldAttrs

    const companyCustomFieldLabels = getCompanyCustomFieldLabels(store.getState())
    const dealCustomFieldLabels = getDealCustomFieldLabels(store.getState())
    const personCustomFieldLabels = getPersonCustomFieldLabels(store.getState())
    this.cfls = cfls || [companyCustomFieldLabels, dealCustomFieldLabels, personCustomFieldLabels].flat()
  }

  changes (newAttrs) {
    const deleteKeys = Object.keys(newAttrs).filter(k => {
      if (this.isEmpty(this.oldAttrs[k])) return this.isEmpty(newAttrs[k])

      if (k.startsWith('custom_label_')) {
        const cfl = this.cfls.find(cfl => cfl.id.toString() === k.substring(13))
        const type = cfl?.field_type
        if (!type) return false
        if (type === 'date') return this.equalDates(this.oldAttrs[k], newAttrs[k])
        if (type === 'currency' || type === 'numeric') return this.equalNumbers(this.oldAttrs[k], newAttrs[k])
        if (type === 'boolean') return this.equalBooleans(this.oldAttrs[k], newAttrs[k])
        if (type === 'text') return this.equalGeneric(this.oldAttrs[k], newAttrs[k])
        if (type === 'dropdown') return this.equalNumbers(this.oldAttrs[k], newAttrs[k])
        if (type === 'multi_select') return this.equalNumericArrays(this.oldAttrs[k], newAttrs[k])
        if (type === 'single_association') return this.equalAssociationValues(this.oldAttrs[k], newAttrs[k])
        if (type === 'multi_association') return this.equalAssociationArrays(this.oldAttrs[k], newAttrs[k])
      }

      if (typeof this.oldAttrs[k] === 'object') {
        if (Array.isArray(this.oldAttrs[k])) {
          if (typeof this.oldAttrs[k][0] === 'number') return this.equalNumericArrays(this.oldAttrs[k], newAttrs[k])
          return this.equalArrays(this.oldAttrs[k], newAttrs[k])
        }
        newAttrs[k] = new ChangedAttributesFinder(this.oldAttrs[k], this.cfls).changes(newAttrs[k])
        return Object.keys(newAttrs[k]).length === 0
      }
      if (typeof this.oldAttrs[k] === 'number') return this.equalNumbers(this.oldAttrs[k], newAttrs[k])
      if (typeof this.oldAttrs[k] === 'boolean') return this.equalBooleans(this.oldAttrs[k], newAttrs[k])
      return (this.equalGeneric(this.oldAttrs[k], newAttrs[k]))
    })
    deleteKeys.forEach(k => delete newAttrs[k])
    return newAttrs
  }

  isEmpty (v) {
    return (typeof v === 'undefined' || v === 'undefined' || v === null || v === '' || (typeof v === 'object' && Array.isArray(v) && v.length === 0))
  }

  normalizeNumericArray (a) {
    return JSON.stringify((this.isEmpty(a) ? [] : (a instanceof Array ? a : [a])).map(e => Number(e)).sort())
  }

  normalizeAssociationValue (v) {
    if (this.isEmpty(v)) return null
    return (typeof v === 'string') ? (v.indexOf(':') ? Number(v.substring(v.indexOf(':') + 1)) : Number(v)) : v
  }

  normalizeAssociationArray (v) {
    return JSON.stringify((this.isEmpty(v) ? [] : v).map(e => Number(this.normalizeAssociationValue(e))).filter(e => !this.isEmpty(e)).sort())
  }

  equalDates (d1, d2) {
    return !this.isEmpty(d2) && (new Date(d1).getTime() === new Date(d2).getTime())
  }

  equalNumbers (v1, v2) {
    return (this.isEmpty(v1) ? '' : Number(v1)) === (this.isEmpty(v2) ? '' : Number(v2))
  }

  equalGeneric (v1, v2) {
    return (JSON.stringify(this.isEmpty(v1) ? '' : (v1 + '')) === JSON.stringify(this.isEmpty(v2) ? '' : (v2 + '')))
  }

  normalizeBoolean (v) {
    if (v === true || v === 1 || v === '1' || v === 'true') return true
    if (v === false || v === 0 || v === '0' || v === 'false') return false
    return null
  }

  equalBooleans (v1, v2) {
    return this.normalizeBoolean(v1) === this.normalizeBoolean(v2)
  }

  equalAssociationValues (v1, v2) {
    return this.normalizeAssociationValue(v1) === this.normalizeAssociationValue(v2)
  }

  equalAssociationArrays (v1, v2) {
    return !this.isEmpty(v2) && this.normalizeAssociationArray(v1) === this.normalizeAssociationArray(v2)
  }

  equalNumericArrays (a1, a2) {
    return !this.isEmpty(a2) && (this.normalizeNumericArray(a1) === this.normalizeNumericArray(a2))
  }

  equalArrays (a1, a2) {
    return !this.isEmpty(a2) && (JSON.stringify(a1 || []) === JSON.stringify(a2 || []))
  }
}

export default ChangedAttributesFinder
